import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { IoClose } from 'react-icons/io5';
import { useEffect, useState } from 'react';
import { toastSuccess } from '../utils/toasts';
import Input from './atoms/Input';
import Button from './atoms/Button';
import { CustomerDTO, CustomerGroupDTO } from '../models/customer';
import { useEditCustomerMutation } from '../store';
import Select from './atoms/Select/Select';
import { formatCurrency } from '../utils/shared';

interface Props {
  customer: CustomerDTO;
  onClose: () => void;
  editMode: boolean;
  customerGroups: CustomerGroupDTO[];
}

interface FormData {
  first_name: string;
  last_name: string;
  document: string;
  email: string;
  phone: string | undefined;
}

const ViewEditCustomer: React.FC<Props> = ({
  onClose, editMode, customer, customerGroups,
}) => {
  const [customerGroup, setCustomerGroup] = useState<CustomerGroupDTO[]>([]);
  const { control, handleSubmit, formState: { errors } } = useForm<FormData>({
    defaultValues: {
      first_name: customer?.first_name || '',
      last_name: customer?.last_name || '',
      document: customer?.person_id || '',
      email: customer?.email || '',
      phone: customer?.phone || '',
    },
    resolver: yupResolver(yup.object().shape({
      first_name: yup.string().required('El nombre es requerido'),
      last_name: yup.string().required('El apellido es requerido'),
      document: yup.string().required('El DNI es requerido'),
      email: yup.string().email('El correo electrónico no es válido').required('El correo electrónico es requerido'),
      phone: yup.string().optional(),
    })),
  });

  const [editCustomer, { isLoading, isSuccess }] = useEditCustomerMutation();

  useEffect(() => {
    if (customerGroups.length > 0) {
      setCustomerGroup(customer.customer_groups.map((c) => customerGroups.find((cg) => cg.id === c.id) as CustomerGroupDTO));
    }
  }, [customerGroups]);

  useEffect(() => {
    if (isSuccess) {
      onClose();
      toastSuccess('Cliente editado correctamente');
    }
  }, [isSuccess]);

  const onSubmit = (data: FormData) => {
    editCustomer({
      businessId: customer.business_id,
      data: {
        id: customer.id,
        first_name: data.first_name,
        last_name: data.last_name,
        person_id: data.document,
        email: data.email,
        phone: data.phone,
        customer_group_ids: customerGroup.map((c) => c.id),
      },
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-4 w-full'>
      <h1>{editMode ? 'Editar datos del cliente' : 'Ver datos del cliente'}</h1>
      <Controller
        name="first_name"
        control={control}
        render={({ field }) => (
          editMode ? (
            <Input
              {...field}
              label="Nombre *"
              labelAbove
              id="first_name"
              errMsg={errors.first_name?.message}
              disabled={!editMode}
            />
          ) : (
            <div>
              <p className="text-left font-sm font-medium mb-1">Nombre *</p>
              <p className="text-text-primary">{field.value}</p>
            </div>
          )
        )}
      />
      <Controller
        name="last_name"
        control={control}
        render={({ field }) => (
          editMode ? (
            <Input
              {...field}
              label="Apellido *"
              labelAbove
              id="last_name"
              errMsg={errors.last_name?.message}
              disabled={!editMode}
            />
          ) : (
            <div>
              <p className="text-left font-sm font-medium mb-1">Apellido *</p>
              <p className="text-text-primary">{field.value}</p>
            </div>
          )
        )}
      />
      <Controller
        name="document"
        control={control}
        render={({ field }) => (
          editMode ? (
            <Input
              {...field}
              label="DNI *"
              labelAbove
              id="document"
              errMsg={errors.document?.message}
              disabled={!editMode}
            />
          ) : (
            <div>
              <p className="text-left font-sm font-medium mb-1">DNI *</p>
              <p className="text-text-primary">{field.value}</p>
            </div>
          )
        )}
      />
      <Controller
        name="email"
        control={control}
        render={({ field }) => (
          editMode ? (
            <Input
              {...field}
              label="Correo electrónico *"
              labelAbove
              id="email"
              errMsg={errors.email?.message}
              disabled={!editMode}
            />
          ) : (
            <div>
              <p className="text-left font-sm font-medium mb-1">Correo electrónico *</p>
              <p className="text-text-primary">{field.value}</p>
            </div>
          )
        )}
      />
      <Controller
        name="phone"
        control={control}
        render={({ field }) => (
          editMode ? (
            <Input
              {...field}
              label="Número de celular"
              labelAbove
              id="phone"
              errMsg={errors.phone?.message}
              disabled={!editMode}
              placeholder=' '
            />
          ) : (
            <div>
              <p className="text-left font-sm font-medium mb-1">Número de celular</p>
              <p className="text-text-primary">{field.value || '-'}</p>
            </div>
          )
        )}
      />
      <div className='flex flex-col gap-2'>
        <div>
          <p className="text-left font-sm font-medium mb-1">Grupo</p>
          {editMode && <div className='max-w-lg'>
            <Select
              id='customer_group'
              disabled={!editMode}
              options={
                customerGroups
                  .filter((c) => !customerGroup.some((cg) => cg.id === c.id))
                  ?.map((c) => ({
                    label: c.name || '',
                    value: c.id || '',
                  })) || []
              }
              placeholder="Selecciona el grupo"
              onChange={(v) => editMode && setCustomerGroup([...customerGroup, customerGroups?.find((c) => c.id === v.target.value) as CustomerGroupDTO])}
            />
          </div>}
          {customerGroup && customerGroup.length > 0 && (
            <div className='flex flex-row gap-2 flex-wrap mt-2'>
              {customerGroup.map((e) => (
                <div
                  key={e.id}
                  className="p-1 bg-surface-secondary rounded-md border border-border-primary w-fit flex items-center gap-2"
                  onClick={() => editMode && setCustomerGroup(customerGroup.filter((se) => se.id !== e.id))}>
                  {e.name}
                  {editMode && <IoClose className="h-4 w-4" />}
                </div>))}
            </div>
          )}
        </div>
        {!editMode && <div>
          {/* <div className='flex flex-col gap-2 border border-border-tertiary rounded-md p-2 w-full'>
          <div className='flex flex-row gap-2 justify-between border-y border-border-tertiary py-2 px-4'>
            <div>Evento</div>
            <div>Fecha de evento</div>
          </div>
        </div> */}
          <div className='flex flex-col'>
            {/* <div className='flex flex-row justify-between'>
            <div>Eventos asistidos</div>
            <div>10</div>
          </div> */}
            <div className='flex flex-row justify-between'>
              <div>Entradas compradas</div>
              <div>{customer.total_tickets_purchased}</div>
            </div>
            <div className='flex flex-row justify-between'>
              <div>Total gastado</div>
              <div>{formatCurrency(customer.total_spent, 'no-decimals')}</div>
            </div>
          </div>
        </div>}
      </div>
      <div className='flex gap-2'>
        <Button variant="secondary" onClick={onClose}>Cancelar</Button>
        <Button type={editMode ? 'submit' : 'button'} loading={isLoading} onClick={() => !editMode && onClose()}>{editMode ? 'Guardar' : 'Aceptar'}</Button>
      </div>
    </form>
  );
};

export default ViewEditCustomer;
